import { Button, Input, Modal, Surface, Table, Td, Th, Tr } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { StaffOrderStatus } from '@warehouse/shared';
import { ArrowLeft } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { usePermission } from '../../../auth/auth-store';
import { FullPageSpinner } from '../../../components/FullPageSpinner';
import { PageHeader } from '../../../components/PageHeader';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { ORDER_QUERY_KEYS, staffOrdersApi } from '../api';
import { StaffStatusBadge } from '../components/StatusBadge';

/** One staff order: lines, the two status steps and the collected-by capture. */
export function StaffOrderDetailPage(): ReactNode {
  const { t } = useTranslation(['salesOrders', 'common']);
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const canManage = usePermission('orders.staff.manage');
  const [collecting, setCollecting] = useState(false);

  const orderQuery = useQuery({
    queryKey: ORDER_QUERY_KEYS.detail(id),
    queryFn: () => staffOrdersApi.detail(id),
    enabled: id.length > 0,
  });
  const historyQuery = useQuery({
    queryKey: ORDER_QUERY_KEYS.history(id),
    queryFn: () => staffOrdersApi.history(id),
    enabled: id.length > 0,
  });

  useLiveQuery(['sales-orders'], ORDER_QUERY_KEYS.detail(id), {
    filter: (event) => event.entityId === id,
  });

  const statusMutation = useMutation({
    mutationFn: (input: { status: StaffOrderStatus; collectedBy?: string }) =>
      staffOrdersApi.setStatus(id, input.status, input.collectedBy),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
      void queryClient.invalidateQueries({ queryKey: ['staff-orders'] });
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  if (orderQuery.isLoading) return <FullPageSpinner />;
  const order = orderQuery.data;
  if (!order) return null;

  const staffStatus = (order.staffStatus ?? 'registered') as StaffOrderStatus;

  return (
    <>
      <PageHeader
        title={t('salesOrders:detail.title', { orderNo: order.orderNo })}
        subtitle={`${order.restaurantName} · ${order.orderDate}`}
        breadcrumb={
          <Link to="/orders/staff" className="inline-flex items-center gap-1 hover:text-ink">
            <ArrowLeft className="size-3" aria-hidden />
            {t('salesOrders:staff.title')}
          </Link>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <StaffStatusBadge status={staffStatus} />
            {canManage && staffStatus === 'registered' ? (
              <Button
                variant="secondary"
                loading={statusMutation.isPending}
                onClick={() => {
                  statusMutation.mutate({ status: 'ready' });
                }}
              >
                {t('salesOrders:staff.actions.markReady')}
              </Button>
            ) : null}
            {canManage && staffStatus !== 'collected' ? (
              <Button
                onClick={() => {
                  setCollecting(true);
                }}
              >
                {t('salesOrders:staff.actions.markCollected')}
              </Button>
            ) : null}
          </div>
        }
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Surface className="p-5 lg:col-span-2">
          <h2 className="mb-3 font-medium">{t('salesOrders:detail.linesTitle')}</h2>
          <div className="overflow-x-auto">
            <Table>
              <thead>
                <tr>
                  <Th>{t('salesOrders:detail.columns.articleNo')}</Th>
                  <Th>{t('salesOrders:detail.columns.article')}</Th>
                  <Th className="text-right">{t('salesOrders:detail.columns.qty')}</Th>
                  <Th>{t('salesOrders:detail.columns.unit')}</Th>
                </tr>
              </thead>
              <tbody>
                {order.lines.map((line) => (
                  <Tr key={line.id}>
                    <Td className="font-mono text-xs">{line.articleNo}</Td>
                    <Td>{line.articleName}</Td>
                    <Td className="text-right">{line.qty}</Td>
                    <Td>{line.unitCode}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Surface>

        <div className="flex flex-col gap-4">
          <Surface className="flex flex-col gap-2 p-5 text-sm">
            <Row label={t('salesOrders:staff.columns.unit')} value={order.restaurantName} />
            <Row label={t('salesOrders:staff.columns.collectedBy')} value={order.collectedBy ?? '—'} />
            <Row
              label={t('salesOrders:staff.columns.collectedAt')}
              value={order.collectedAt ? new Date(order.collectedAt).toLocaleString('sv-SE') : '—'}
            />
            <Row label={t('salesOrders:detail.note')} value={order.note ?? t('salesOrders:detail.noNote')} />
          </Surface>

          <Surface className="p-5">
            <h2 className="mb-3 font-medium">{t('salesOrders:detail.history')}</h2>
            {(historyQuery.data?.items.length ?? 0) === 0 ? (
              <p className="text-sm text-ink-muted">{t('salesOrders:detail.historyEmpty')}</p>
            ) : (
              <ul className="flex flex-col gap-2 text-sm">
                {historyQuery.data?.items.map((entry, index) => (
                  <li key={`${entry.at}-${index}`} className="flex flex-col">
                    <span className="font-mono text-xs text-ink-muted">
                      {new Date(entry.at).toLocaleString('sv-SE')}
                    </span>
                    <span>
                      {entry.action}
                      {entry.userName ? ` · ${entry.userName}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Surface>
        </div>
      </div>

      {collecting ? (
        <CollectModal
          orderNo={order.orderNo}
          initial={order.collectedBy ?? ''}
          saving={statusMutation.isPending}
          onClose={() => {
            setCollecting(false);
          }}
          onConfirm={(collectedBy) => {
            statusMutation.mutate(
              { status: 'collected', collectedBy },
              {
                onSuccess: () => {
                  toastSuccess(t('salesOrders:staff.collect.done'));
                  setCollecting(false);
                },
              },
            );
          }}
        />
      ) : null}
    </>
  );
}

function Row({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-ink-muted">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}

function CollectModal({
  orderNo,
  initial,
  saving,
  onClose,
  onConfirm,
}: {
  orderNo: string;
  initial: string;
  saving: boolean;
  onClose: () => void;
  onConfirm: (collectedBy: string) => void;
}): ReactNode {
  const { t } = useTranslation(['salesOrders', 'common']);
  const [value, setValue] = useState(initial);
  const [error, setError] = useState<string | null>(null);

  return (
    <Modal open onClose={onClose} title={t('salesOrders:staff.collect.title')} width={440}>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink-muted">{t('salesOrders:staff.collect.message', { orderNo })}</p>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('salesOrders:staff.collect.collectedBy')}</span>
          <Input
            autoFocus
            value={value}
            onChange={(event) => {
              setValue(event.target.value);
            }}
          />
        </label>
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            loading={saving}
            onClick={() => {
              if (!value.trim()) {
                setError(t('salesOrders:staff.collect.required'));
                return;
              }
              setError(null);
              onConfirm(value.trim());
            }}
          >
            {t('salesOrders:staff.collect.confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
